import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { getUserAgents, getInteractedAgents } from '../../api/agents/agents';
import AgentCard from './AgentCard';

import styles from './styles/AgentsList.module.css';

const AgentsList = ({ type = 'owned', refreshSignal = 0 }) => {
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const seenIdsRef = useRef(new Set());

    const fetchAgents = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = type === 'interacted'
                ? await getInteractedAgents()
                : await getUserAgents();
            setAgents(data);
        } catch (err) {
            console.error('Error fetching agents:', err);
            setError('Failed to load agents');
        } finally {
            setIsLoading(false);
        }
    }, [type]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    // Re-fetch when refreshSignal changes (e.g., after successful creation)
    useEffect(() => {
        if (refreshSignal > 0) {
            fetchAgents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    // After agents are loaded/rendered, mark them as seen so they won't re-animate next time
    useEffect(() => {
        for (const a of agents) {
            seenIdsRef.current.add(a.id);
        }
    }, [agents]);

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
            },
        },
    };

    return (
        <div className={styles.container}>
            {agents.length > 0 && !isLoading && (
                <motion.div
                    className={styles.grid}
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {agents.map((agent, index) => {
                        const isNew = !seenIdsRef.current.has(agent.id);
                        return (
                            <motion.div
                                key={agent.id}
                                initial={isNew ? { opacity: 0, y: 30 } : false}
                                animate={isNew ? { opacity: 1, y: 0 } : {}}
                                transition={isNew ? {
                                    duration: 0.5,
                                    delay: index * 0.1,
                                    ease: "easeOut",
                                } : {}}
                            >
                                <AgentCard agent={agent} mode={type} />
                            </motion.div>
                        );
                    })}
                </motion.div>
            )}
            {isLoading && (
                <motion.div
                    className={styles.text}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    Loading agents...
                </motion.div>
            )}
            {agents.length === 0 && !isLoading && (
                <motion.div
                    className={styles.text}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {type === 'owned' 
                        ? 'No agents found. Create your first agent to get started.'
                        : "You haven't chatted with any non-owned agents yet. Start a conversation to see them here."}
                </motion.div>
            )}
        </div>
    );
};

export default memo(AgentsList);
